import { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import type { ModalProps } from "./Modal.types";
import styles from "./Modal.module.css";

export const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  closeButtonDisabled = false,
  unconstrained = false,
  className,
  overlayClassName,
  headerClassName,
  titleClassName,
  bodyClassName,
  ariaLabel = "Dialog",
  hideBackdrop = false,
}: ModalProps) => {
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen || typeof document === "undefined") return null;

  const hasHeader = Boolean(title) || showCloseButton;

  const defaultHeaderClass =
    `${styles.header} ${!title && showCloseButton ? styles.headerCloseOnly : ""}`.trim();

  const panel = (
    <div
      className={`${styles.overlay} ${overlayClassName ?? ""}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : ariaLabel}
      onClick={hideBackdrop && closeOnBackdropClick ? onClose : undefined}
    >
      {!hideBackdrop && (
        <div
          className={styles.backdrop}
          onClick={closeOnBackdropClick ? onClose : undefined}
          aria-hidden="true"
        />
      )}
      <div
        className={`${styles.panel} ${unconstrained ? styles.panelUnconstrained : ""} ${className ?? ""}`.trim()}
        onClick={(event) => event.stopPropagation()}
      >
        {hasHeader && (
          <div className={headerClassName ?? defaultHeaderClass}>
            {title ? (
              typeof title === "string" ? (
                <h2 id={titleId} className={titleClassName ?? styles.title}>
                  {title}
                </h2>
              ) : (
                <div id={titleId} className={titleClassName ?? styles.title}>
                  {title}
                </div>
              )
            ) : null}
            {showCloseButton && (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                disabled={closeButtonDisabled}
                aria-label="Close dialog"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className={`${styles.body} ${bodyClassName ?? ""}`.trim()}>{children}</div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};
